import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './app/auth/useAuth';
import { TelaAfericao } from './app/afericao/TelaAfericao';
import { HomeScreen } from './app/components/HomeScreen';
import { LoadingEmpresaScreen } from './app/components/LoadingEmpresaScreen';
import { ForaDaAreaScreen } from './app/components/ForaDaAreaScreen';
import { BootstrapDebug } from './app/components/BootstrapDebug';
import { bootstrapFlow, loadEmpresasFromServer } from './app/bootstrap/bootstrapFlow';
import { iniciarAfericao } from './app/afericao/iniciarAfericao';
import { getAfericaoPorId } from './app/offline/api';
import { useEffect, useState, useRef } from 'react';

function CallbackHandler() {
  const { handleAuthCallback } = useAuth();
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Evitar processar múltiplas vezes
    if (hasProcessed.current || isProcessing) {
      console.log('[Router] Callback já foi processado ou está em processamento, ignorando...');
      return;
    }

    hasProcessed.current = true;
    setIsProcessing(true);
    console.log('[Router] Processando callback OAuth...');
    
    handleAuthCallback()
      .then((user) => {
        console.log('[Router] Callback processado com sucesso, redirecionando...');
        // Limpar parâmetros da URL antes de redirecionar
        window.history.replaceState({}, document.title, '/');
        // Redirecionar para a página principal após login bem-sucedido
        window.location.href = '/';
      })
      .catch((err) => {
        console.error('[Router] Erro no callback:', err);
        console.error('[Router] Stack trace:', err.stack);
        
        // Se o erro for relacionado a estado OAuth perdido ou código já usado,
        // limpar storage e redirecionar para login
        const errorMessage = err.message || err.error || '';
        if (errorMessage.includes('No matching state') || 
            errorMessage.includes('already been redeemed') ||
            errorMessage.includes('invalid_grant')) {
          console.log('[Router] Erro de estado OAuth, limpando storage e redirecionando para login...');
          // Limpar storage do OIDC
          localStorage.removeItem(`oidc.user:${import.meta.env.VITE_AUTH_URL || 'https://localhost:5001'}:${import.meta.env.VITE_CLIENT_ID || 'afericoes'}`);
          // Limpar parâmetros da URL
          window.history.replaceState({}, document.title, '/');
          // Redirecionar para login após um pequeno delay
          setTimeout(() => {
            window.location.href = '/';
          }, 1000);
          return;
        }
        
        setError(err.message || 'Erro ao processar autenticação');
        setIsProcessing(false);
      });
  }, []); // Remover handleAuthCallback da dependência para evitar múltiplas execuções

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Erro na Autenticação</h2>
        <p>{error}</p>
        <button onClick={() => (window.location.href = '/')}>Voltar</button>
      </div>
    );
  }

  return <LoadingEmpresaScreen />;
}

export function Router() {
  console.log('📍 [Router] Componente Router sendo renderizado');
  console.log('%c[Router] ====== ROUTER RENDERIZADO ======', 'background: #009900; color: white; font-size: 14px; padding: 4px;');
  
  console.log('[Router] Chamando useAuth()...');
  const { isAuthenticated, isLoading, login, user } = useAuth();
  console.log('[Router] useAuth retornou:', { isAuthenticated, isLoading });
  const location = useLocation();
  const navigate = useNavigate();
  const [bootstrapState, setBootstrapState] = useState({
    loading: false,
    error: null,
    empresa: null,
    afericao: null,
    foraDaArea: false
  });
  const [currentAfericaoId, setCurrentAfericaoId] = useState(null);

  console.log('[Router] Estado inicial:', {
    isAuthenticated,
    isLoading,
    pathname: location.pathname,
    bootstrapLoading: bootstrapState.loading
  });

  // Tratar callback OAuth
  if (location.pathname === '/callback') {
    return <CallbackHandler />;
  }

  // Redirecionar para login se não autenticado
  useEffect(() => {
    console.log('[Router] useEffect de autenticação executado:', {
      isLoading,
      isAuthenticated,
      pathname: location.pathname
    });
    
    if (!isLoading && !isAuthenticated && location.pathname !== '/callback') {
      console.log('[Router] Não autenticado, redirecionando para login...');
      // Usar setTimeout para evitar atualização durante render
      const timeoutId = setTimeout(() => {
        try {
          login();
        } catch (error) {
          console.error('[Router] Erro ao chamar login():', error);
        }
      }, 0);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isLoading, isAuthenticated, location.pathname, login]);

  // Carregar empresas de forma assíncrona quando autenticado (apenas para atualizar, não bloqueia)
  useEffect(() => {
    if (isAuthenticated && !isLoading && location.pathname !== '/callback') {
      console.log('[Router] Autenticado, iniciando atualização assíncrona de empresas...');
      // Atualizar empresas em background (não bloqueia a UI)
      // O bootstrapFlow já carrega síncrono na primeira vez se necessário
      loadEmpresasFromServer(true).catch(err => {
        console.warn('[Router] Erro ao atualizar empresas em background:', err);
      });
    }
  }, [isAuthenticated, isLoading, location.pathname]);

  // Executar bootstrap quando autenticado
  useEffect(() => {
    let isMounted = true;
    
    if (isAuthenticated && !isLoading && location.pathname !== '/callback') {
      console.log('[Router] Autenticado, iniciando bootstrap...');
      setBootstrapState((prev) => ({ ...prev, loading: true }));
      
      // Timeout de segurança para o bootstrap
      const timeoutId = setTimeout(() => {
        if (isMounted) {
          console.error('[Router] Timeout no bootstrap após 60 segundos');
          setBootstrapState({
            loading: false,
            error: 'Timeout: O carregamento está demorando muito. Verifique sua conexão e tente novamente.',
            empresa: null,
            afericao: null,
            foraDaArea: false
          });
        }
      }, 60000); // 60 segundos
      
      bootstrapFlow()
        .then((result) => {
          clearTimeout(timeoutId);
          if (!isMounted) return;
          
          console.log('[Router] Bootstrap concluído:', {
            hasEmpresa: !!result.empresa,
            hasAfericao: !!result.afericao,
            foraDaArea: result.foraDaArea
          });
          setBootstrapState({
            loading: false,
            error: null,
            empresa: result.empresa,
            afericao: result.afericao,
            foraDaArea: result.foraDaArea || false
          });
        })
        .catch((error) => {
          clearTimeout(timeoutId);
          if (!isMounted) return;
          
          console.error('[Router] Erro no bootstrap:', error);
          console.error('[Router] Stack trace:', error.stack);
          console.error('[Router] Detalhes do erro:', {
            message: error.message,
            name: error.name,
            cause: error.cause
          });
          setBootstrapState({
            loading: false,
            error: error.message || 'Erro desconhecido no bootstrap',
            empresa: null,
            afericao: null,
            foraDaArea: false
          });
        });
    }
    
    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, isLoading, location.pathname]);

  if (isLoading || (isAuthenticated && bootstrapState.loading)) {
    return <LoadingEmpresaScreen />;
  }

  if (!isAuthenticated) {
    return <LoadingEmpresaScreen />;
  }

  if (bootstrapState.foraDaArea) {
    return <ForaDaAreaScreen />;
  }

  if (bootstrapState.error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Erro</h2>
        <p>{bootstrapState.error}</p>
        <button onClick={() => window.location.reload()}>Tentar Novamente</button>
      </div>
    );
  }

  const handleIniciarAfericao = async () => {
    try {
      setBootstrapState((prev) => ({ ...prev, loading: true }));
      const afericaoId = await iniciarAfericao(bootstrapState.empresa);
      setCurrentAfericaoId(afericaoId);
      navigate('/afericao');
    } catch (error) {
      console.error('Erro ao iniciar aferição:', error);
      alert('Erro ao iniciar aferição. Tente novamente.');
    } finally {
      setBootstrapState((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleContinuarAfericao = async (afericaoId) => {
    setCurrentAfericaoId(afericaoId);
    navigate('/afericao');
  };

  const handleAfericaoFinalizada = () => {
    // Voltar para a tela inicial e recarregar bootstrap
    setCurrentAfericaoId(null);
    navigate('/');
    
    // Recarregar dados
    if (isAuthenticated && !isLoading) {
      setBootstrapState((prev) => ({ ...prev, loading: true }));
      bootstrapFlow()
        .then((result) => {
          setBootstrapState({
            loading: false,
            error: null,
            empresa: result.empresa,
            afericao: result.afericao,
            foraDaArea: result.foraDaArea || false
          });
        })
        .catch((error) => {
          console.error('[Router] Erro ao recarregar bootstrap:', error);
          setBootstrapState((prev) => ({
            ...prev,
            loading: false,
            error: error.message || 'Erro ao recarregar dados'
          }));
        });
    }
  };

  if (bootstrapState.empresa) {
    return (
      <>
        <Routes>
          <Route
            path="/"
            element={
              <HomeScreen
                empresa={bootstrapState.empresa}
                afericaoAberta={bootstrapState.afericao}
                onIniciarAfericao={handleIniciarAfericao}
                onContinuarAfericao={handleContinuarAfericao}
                loading={bootstrapState.loading}
              />
            }
          />
          <Route
            path="/afericao"
            element={
              currentAfericaoId ? (
                <TelaAfericao
                  empresa={bootstrapState.empresa}
                  afericaoId={currentAfericaoId}
                  onFinalizada={handleAfericaoFinalizada}
                />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        {import.meta.env.DEV && (
          <BootstrapDebug
            bootstrapState={bootstrapState}
            authState={{ isLoading, isAuthenticated, user }}
          />
        )}
      </>
    );
  }

  return (
    <>
      <LoadingEmpresaScreen />
      {import.meta.env.DEV && (
        <BootstrapDebug
          bootstrapState={bootstrapState}
          authState={{ isLoading, isAuthenticated, user }}
        />
      )}
    </>
  );
}

